/**
 * Pure query-shape helpers for the Algolia-backed brand search.
 *
 * Kept out of `algolia.ts` for the same reason `referenceListQuery.ts` is: the
 * parameter building and the local-fallback matching are unit-tested without
 * pulling in the Algolia client (and the `import.meta.env` credential read that
 * happens at its module scope).
 */

import { normalizeText } from '../utils/text';

/**
 * Attributes a brand hit carries. Every one is present on the live
 * `watchkeeper_brands` index (watchlock #861 — verified against it); requesting
 * an attribute the index doesn't hold is silently dropped rather than an error,
 * so this list is only as good as the indexer it mirrors.
 */
export const BRAND_SEARCH_ATTRIBUTES = [
  'objectID',
  'name',
  'displayName',
  'country',
  'parentOrg',
  'alternativeNames',
  'abbreviations',
  'hasLogo',
  'isDisabled',
  'isPromoted',
] as const;

/**
 * Facets the brands index can group by. All five are declared
 * `attributesForFaceting` on the live index (verified: a `facets: ['*']` query
 * returns exactly these). Requesting a NON-faceted attribute 400s the whole
 * search, so this list must only ever grow alongside the index config, which
 * lives server-side in watchlock.
 */
export const BRAND_SEARCH_FACETS = [
  'country',
  'parentOrg',
  'isDisabled',
  'isPromoted',
  'isMicroBrand',
] as const;

export type BrandSearchFacetName = (typeof BRAND_SEARCH_FACETS)[number];

/** Filters the brand search can apply. All are plain facet filters. */
export interface BrandSearchFilters {
  country?: string;
  parentOrg?: string;
  isDisabled?: boolean;
}

/**
 * Algolia search parameters for one page of brand results.
 *
 * Each filter is its own `facetFilters` group, so groups AND together — country
 * plus parentOrg narrows to the intersection.
 *
 * `facets` is OPT-IN rather than always-on: the brand pickers call this on every
 * (debounced) keystroke and none of them render facet counts, so computing and
 * shipping them by default would be payload nobody reads.
 *
 * `attributesToHighlight: []` because no brand surface renders highlight markup.
 * `analytics: false` keeps admin browsing out of the app's search analytics.
 * `cacheable: false` bypasses the SDK's in-memory response cache, so a brand
 * renamed in the editor is findable by its new name without a page reload.
 */
export function buildBrandSearchParams(
  filters: BrandSearchFilters,
  page: number,
  hitsPerPage: number,
  opts?: {
    attributes?: readonly string[];
    facets?: readonly BrandSearchFacetName[];
  },
): Record<string, unknown> {
  const facetFilters: string[][] = [];
  if (filters.country) facetFilters.push([`country:${filters.country}`]);
  if (filters.parentOrg) facetFilters.push([`parentOrg:${filters.parentOrg}`]);
  if (filters.isDisabled !== undefined) {
    facetFilters.push([`isDisabled:${filters.isDisabled}`]);
  }

  const params: Record<string, unknown> = {
    hitsPerPage,
    page,
    analytics: false,
    cacheable: false,
    attributesToRetrieve: [...(opts?.attributes ?? BRAND_SEARCH_ATTRIBUTES)],
    attributesToHighlight: [],
  };
  if (facetFilters.length > 0) params.facetFilters = facetFilters;
  if (opts?.facets && opts.facets.length > 0) {
    params.facets = [...opts.facets];
    params.maxValuesPerFacet = 1000;
  }
  return params;
}

/**
 * The shape every brand-ish list item in the admin shares. Deliberately loose:
 * the pickers pass `WatchBrand[]`, the calibre-tier form passes a
 * `{ id, name }[]` projection, and both have to run through the same matcher.
 */
export interface BrandSearchable {
  id: string;
  name?: string;
  displayName?: string;
  alternativeNames?: string[];
  abbreviations?: string[];
}

/**
 * The pre-Algolia substring match, kept as the offline fallback.
 *
 * Covers the union of what the individual pickers used to check on their own —
 * name, display name, doc id, alternative names, abbreviations — so no picker
 * matches LESS than it did before when Algolia is unreachable. Accent-folded via
 * `normalizeText`, which is what the brand listing already did.
 */
export function localBrandMatches(
  brand: BrandSearchable,
  term: string,
): boolean {
  const needle = normalizeText(term.trim());
  if (!needle) return true;
  const haystack = [
    brand.name ?? '',
    brand.displayName ?? '',
    brand.id,
    ...(brand.alternativeNames ?? []),
    ...(brand.abbreviations ?? []),
  ].join(' ');
  return normalizeText(haystack).includes(needle);
}

/**
 * Resolve Algolia hit ids back to the caller's own brand objects.
 *
 * Algolia decides WHICH brands match and in what order; the objects themselves
 * still come from the caller's loaded list, because the index carries a subset
 * of the fields these surfaces render (no ref counts, no status, no timestamps).
 *
 * Two deliberate properties:
 *
 * - A hit with no local object is DROPPED. Every caller's `onChange` hands back
 *   an id its own list has to resolve, so an unresolvable row would be a dead
 *   option.
 * - Local substring matches Algolia did NOT return are APPENDED after the
 *   ranked hits. The index is eventually consistent (a brand created seconds ago
 *   isn't in it yet) and a disabled brand may be evicted from it entirely, so an
 *   exact substring match must never become unfindable just because Algolia
 *   hasn't caught up. Ranked matches still come first.
 *
 * `hitIds` of `null` means "no Algolia answer" (unconfigured, failed, or still
 * in flight) — the result is then the pure local filter.
 */
export function mergeBrandSearchResults<T extends BrandSearchable>(
  items: T[],
  hitIds: string[] | null,
  term: string,
): T[] {
  if (!term.trim()) return items;
  const byId = new Map<string, T>();
  for (const item of items) byId.set(item.id, item);

  const merged: T[] = [];
  const seen = new Set<string>();
  for (const id of hitIds ?? []) {
    const item = byId.get(id);
    if (item && !seen.has(id)) {
      merged.push(item);
      seen.add(id);
    }
  }
  for (const item of items) {
    if (!seen.has(item.id) && localBrandMatches(item, term)) {
      merged.push(item);
      seen.add(item.id);
    }
  }
  return merged;
}
